import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { 
  ArrowLeft, 
  Building2, 
  ExternalLink,
  Edit,
  Save,
  X,
  Users,
  CreditCard,
  Calendar,
  Mail,
  Globe,
  Settings,
  MoreVertical,
  Trash2
} from 'lucide-react'
import api from '../lib/api'
import { formatDate, formatCurrency, getStatusColor } from '../lib/utils'
import toast from 'react-hot-toast'

export default function BusinessDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')

  const { data: business, isLoading } = useQuery({
    queryKey: ['business', id],
    queryFn: async () => {
      const response = await api.get(`/admin/tenants/${id}`)
      return response.data.data // Backend returns { success, data }
    }
  })

  const { data: subscription } = useQuery({
    queryKey: ['business-subscription', id],
    queryFn: async () => {
      const response = await api.get(`/admin/subscriptions?tenant_id=${id}`)
      return response.data.data // Backend returns { success, data }
    },
    enabled: !!id
  })

  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await api.get('/admin/plans')
      return response.data.data // Backend returns { success, data }
    }
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      name: '',
      subdomain: '',
      email: '',
      status: 'active'
    }
  })

  const updateBusinessMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/admin/tenants/${id}`, data)
      return response.data.data // Backend returns { success, data }
    },
    onSuccess: () => {
      toast.success('Business updated successfully!')
      setIsEditing(false)
      queryClient.invalidateQueries(['business', id])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update business')
    }
  })

  const deleteBusinessMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete(`/admin/tenants/${id}`)
      return response.data // Delete might not return nested data
    },
    onSuccess: () => {
      toast.success('Business deleted successfully!')
      navigate('/businesses')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete business')
    }
  })

  const handleEdit = () => {
    if (business) {
      reset({
        name: business.name,
        subdomain: business.subdomain,
        email: business.email || business.billing_email,
        status: business.status
      })
    }
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    reset()
  }

  const onSubmit = (data) => {
    updateBusinessMutation.mutate(data)
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this business? This action cannot be undone.')) {
      deleteBusinessMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
        </div>
        <div className="card p-6 animate-pulse">
          <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
            <div className="h-4 bg-muted rounded w-1/3"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!business) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground">Business not found</h3>
        <button
          onClick={() => navigate('/businesses')}
          className="btn btn-primary mt-4"
        >
          Back to Businesses
        </button>
      </div>
    )
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Building2 },
    { id: 'subscription', name: 'Subscription', icon: CreditCard },
    { id: 'settings', name: 'Settings', icon: Settings },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/businesses')}
            className="btn btn-ghost btn-sm"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{business.name}</h1>
            <p className="text-muted-foreground">
              {business.subdomain}.easica.com
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`http://${business.subdomain}.easica.com`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline btn-sm"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit Site
          </a>
          {!isEditing ? (
            <button onClick={handleEdit} className="btn btn-primary btn-sm">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleCancel} className="btn btn-outline btn-sm">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={updateBusinessMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Business Information */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Business Information</h3>
              {isEditing ? (
                <form className="space-y-4">
                  <div>
                    <label className="label">Business Name</label>
                    <input
                      {...register('name', { required: 'Name is required' })}
                      className="input mt-1"
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Subdomain</label>
                    <input
                      {...register('subdomain', { required: 'Subdomain is required' })}
                      className="input mt-1"
                    />
                    {errors.subdomain && (
                      <p className="text-sm text-destructive mt-1">{errors.subdomain.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Email</label>
                    <input
                      {...register('email')}
                      type="email"
                      className="input mt-1"
                    />
                  </div>
                  <div>
                    <label className="label">Status</label>
                    <select {...register('status')} className="input mt-1">
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Name:</span>
                    <span>{business.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Subdomain:</span>
                    <span>{business.subdomain}.easica.com</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Email:</span>
                    <span>{business.email || business.billing_email || 'Not set'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(business.status)}`}>
                      {business.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Created:</span>
                    <span>{formatDate(business.created_at)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Usage Statistics */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Usage Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{business.usage?.user_count || 0}</div>
                  <div className="text-sm text-muted-foreground">Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{business.usage?.instructor_count || 0}</div>
                  <div className="text-sm text-muted-foreground">Instructors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{business.usage?.student_count || 0}</div>
                  <div className="text-sm text-muted-foreground">Students</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{business.usage?.sessions_this_month || 0}</div>
                  <div className="text-sm text-muted-foreground">Sessions (30d)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button className="btn btn-outline w-full justify-start">
                  <Users className="h-4 w-4 mr-2" />
                  Manage Users
                </button>
                <button className="btn btn-outline w-full justify-start">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Update Subscription
                </button>
                <button className="btn btn-outline w-full justify-start">
                  <Settings className="h-4 w-4 mr-2" />
                  Business Settings
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-destructive w-full justify-start"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Business
                </button>
              </div>
            </div>

            {/* Current Plan */}
            {subscription?.data?.[0] && (
              <div className="card p-6">
                <h3 className="text-lg font-semibold mb-4">Current Plan</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Plan:</span>
                    <span className="text-sm font-medium">
                      {subscription.data[0].plan_display_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Price:</span>
                    <span className="text-sm font-medium">
                      {formatCurrency(subscription.data[0].plan_price)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.data[0].status)}`}>
                      {subscription.data[0].status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Next Billing:</span>
                    <span className="text-sm font-medium">
                      {formatDate(subscription.data[0].current_period_ends_at)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'subscription' && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Subscription Management</h3>
          {subscription?.data?.[0] ? (
            <div className="space-y-6">
              {/* Subscription Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Current Subscription</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Plan:</span>
                      <span className="text-sm font-medium">
                        {subscription.data[0].plan_display_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Price:</span>
                      <span className="text-sm font-medium">
                        {formatCurrency(subscription.data[0].plan_price)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Billing Cycle:</span>
                      <span className="text-sm font-medium">
                        {subscription.data[0].billing_cycle}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.data[0].status)}`}>
                        {subscription.data[0].status}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Billing Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Current Period:</span>
                      <span className="text-sm font-medium">
                        {formatDate(subscription.data[0].current_period_start)} - {formatDate(subscription.data[0].current_period_ends_at)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Trial End:</span>
                      <span className="text-sm font-medium">
                        {subscription.data[0].trial_end ? formatDate(subscription.data[0].trial_end) : 'No trial'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Created:</span>
                      <span className="text-sm font-medium">
                        {formatDate(subscription.data[0].created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Features */}
              {subscription.data[0].features_json && (
                <div>
                  <h4 className="font-medium mb-2">Plan Features</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {subscription.data[0].features_json.features?.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button className="btn btn-primary">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Change Plan
                </button>
                <button className="btn btn-outline">
                  <Settings className="h-4 w-4 mr-2" />
                  Billing Settings
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h4 className="font-medium mb-2">No Active Subscription</h4>
              <p className="text-sm text-muted-foreground mb-4">
                This business doesn't have an active subscription.
              </p>
              <button className="btn btn-primary">
                Create Subscription
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Business Settings</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-2">Danger Zone</h4>
              <div className="border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-destructive">Delete Business</h5>
                    <p className="text-sm text-muted-foreground">
                      Permanently delete this business and all associated data. This action cannot be undone.
                    </p>
                  </div>
                  <button
                    onClick={handleDelete}
                    className="btn btn-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Business
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
